import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { useLocation } from '@/providers/LocationProvider';
import { Package, TrendingUp, Users, DollarSign } from 'lucide-react';

interface Stats {
  pending: number;
  active: number;
  completed: number;
  revenue: number;
}

export default function AdminHome() {
  const location = useLocation();
  const [stats, setStats] = useState<Stats>({ pending: 0, active: 0, completed: 0, revenue: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        // Get location ID
        const { data: locationData } = await supabase
          .from('locations')
          .select('id')
          .eq('code', location)
          .single();

        if (!locationData) return;

        // Fetch bookings
        const { data: bookings } = await supabase
          .from('bookings')
          .select('status, total_cents')
          .eq('location_id', locationData.id);

        if (bookings) {
          const pending = bookings.filter(b => b.status === 'paid').length;
          const active = bookings.filter(b => 
            ['assigned', 'en_route', 'arrived', 'in_progress'].includes(b.status)
          ).length;
          const completed = bookings.filter(b => b.status === 'completed').length;
          const revenue = bookings.reduce((sum, b) => sum + (b.total_cents || 0), 0);

          setStats({ pending, active, completed, revenue });
        }
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [location]);

  const statCards = [
    {
      title: 'Pending Assignment',
      value: stats.pending,
      icon: Package,
      description: 'Awaiting filmmaker assignment',
    },
    {
      title: 'Active Jobs',
      value: stats.active,
      icon: TrendingUp,
      description: 'Currently in progress',
    },
    {
      title: 'Completed',
      value: stats.completed,
      icon: Users,
      description: 'Successfully finished',
    },
    {
      title: 'Total Revenue',
      value: `$${(stats.revenue / 100).toFixed(2)}`,
      icon: DollarSign,
      description: 'Across all bookings',
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold tracking-tight">Dashboard</h2>
        <p className="text-muted-foreground">
          Overview of your {location} operations
        </p>
      </div>

      {/* KPI Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.title}>
              <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
                <CardTitle className="text-sm font-medium">
                  {stat.title}
                </CardTitle>
                <Icon className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {loading ? '...' : stat.value}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {stat.description}
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Placeholder for upcoming features */}
      <Card>
        <CardHeader>
          <CardTitle>Upcoming Sessions</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Session list coming soon...
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
